/**
 * Slide Drawer Menu
 * Handles hamburger menu and slide-over drawer functionality
 */

class SlideDrawer {
  constructor() {
    this.drawer = document.querySelector(".slide-drawer");
    this.overlay = document.querySelector(".drawer-overlay");
    this.hamburgerBtn = document.querySelector(".hamburger-menu");
    this.closeBtn = document.querySelector(".drawer-close");
    this.body = document.body;
    this.isOpen = false;

    // Check if required elements exist
    if (!this.drawer || !this.overlay || !this.hamburgerBtn) {
      console.warn("SlideDrawer: Required elements not found");
      return;
    }

    this.init();
  }

  init() {
    // Bind event listeners
    this.hamburgerBtn.addEventListener("click", (e) => {
      e.preventDefault();
      this.toggle();
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });
    }

    // Close drawer when clicking overlay
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Handle escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Handle submenu toggles
    this.initSubmenuToggles();

    // Handle navigation links for smooth scrolling
    this.initNavigationLinks();

    // Handle window resize
    window.addEventListener("resize", () => {
      if (window.innerWidth > 768 && this.isOpen) {
        // Optional: Close drawer on desktop resize
        // this.close();
      }
    });
  }

  initSubmenuToggles() {
    const submenuToggles = this.drawer.querySelectorAll(".drawer-nav-toggle");

    submenuToggles.forEach((toggle) => {
      toggle.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleSubmenu(toggle);
      });
    });
  }

  initNavigationLinks() {
    const navLinks = this.drawer.querySelectorAll(
      ".drawer-nav a, .drawer-submenu-link"
    );

    navLinks.forEach((link) => {
      link.addEventListener("click", (e) => {
        this.handleNavigationClick(e, link);
      });
    });
  }

  handleNavigationClick(e, link) {
    const href = link.getAttribute("href");

    // Check if this is a hash link (starts with #)
    if (href && href.startsWith("#")) {
      const targetId = href.substring(1); // Remove the #
      const targetElement = document.getElementById(targetId);

      // If target element exists on current page, scroll to it
      if (targetElement) {
        e.preventDefault();
        this.scrollToSection(targetElement);
        return;
      }
    }

    // Check if this is a link to current page with hash
    if (href && href.includes("#")) {
      const url = new URL(href, window.location.origin);
      const currentPath = window.location.pathname;

      // If the path matches current page but has a hash
      if (url.pathname === currentPath && url.hash) {
        const targetId = url.hash.substring(1); // Remove the #
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          e.preventDefault();
          this.scrollToSection(targetElement);
          return;
        }
      }
    }

    // For all other links, just close the drawer and let normal navigation happen
    this.close();
  }

  scrollToSection(targetElement) {
    // Close the drawer first
    this.close();

    // Small delay to let drawer close animation start
    setTimeout(() => {
      // Smooth scroll to the target element
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      // Update URL hash without triggering scroll
      if (history.pushState) {
        const newUrl =
          window.location.pathname +
          window.location.search +
          "#" +
          targetElement.id;
        history.pushState(null, null, newUrl);
      }
    }, 100);
  }

  toggleSubmenu(toggle) {
    const submenu = toggle
      .closest(".drawer-nav-item")
      .querySelector(".drawer-submenu");
    const isExpanded = toggle.classList.contains("expanded");

    if (isExpanded) {
      // Collapsing current submenu - don't close others
      toggle.classList.remove("expanded");
      submenu.classList.remove("expanded");
      toggle.setAttribute("aria-expanded", "false");
    } else {
      // Expanding - close other open submenus first
      this.closeAllSubmenus(toggle);

      toggle.classList.add("expanded");
      submenu.classList.add("expanded");
      toggle.setAttribute("aria-expanded", "true");
    }
  }

  closeAllSubmenus(exceptToggle = null) {
    const toggles = this.drawer.querySelectorAll(".drawer-nav-toggle");
    const submenus = this.drawer.querySelectorAll(".drawer-submenu");

    toggles.forEach((toggle) => {
      // Skip the toggle being opened and SHOP/OCTANE submenus
      const isAlwaysExpanded =
        toggle.textContent.includes("SHOP") ||
        toggle.textContent.includes("OCTANE");
      if (toggle !== exceptToggle && !isAlwaysExpanded) {
        toggle.classList.remove("expanded");
        toggle.setAttribute("aria-expanded", "false");
      }
    });

    submenus.forEach((submenu) => {
      // Don't close SHOP/OCTANE submenus or the one being opened
      const parentToggle = submenu.previousElementSibling;
      const isAlwaysExpanded =
        parentToggle &&
        (parentToggle.textContent.includes("SHOP") ||
          parentToggle.textContent.includes("OCTANE"));
      if (parentToggle !== exceptToggle && !isAlwaysExpanded) {
        submenu.classList.remove("expanded");
      }
    });
  }

  expandShopSubmenu() {
    const toggles = this.drawer.querySelectorAll(".drawer-nav-toggle");
    toggles.forEach((toggle) => {
      if (
        toggle.textContent.includes("SHOP") ||
        toggle.textContent.includes("OCTANE")
      ) {
        const submenu = toggle
          .closest(".drawer-nav-item")
          .querySelector(".drawer-submenu");
        toggle.classList.add("expanded");
        toggle.setAttribute("aria-expanded", "true");
        if (submenu) {
          submenu.classList.add("expanded");
        }
      }
    });
  }

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.drawer.classList.add("active");
    this.overlay.classList.add("active");
    this.hamburgerBtn.classList.add("active");
    this.body.classList.add("drawer-open");

    // Expand SHOP submenu by default
    this.expandShopSubmenu();

    // Focus management for accessibility
    this.drawer.focus();

    // Trap focus within drawer
    this.trapFocus();

    // Set ARIA attributes
    this.hamburgerBtn.setAttribute("aria-expanded", "true");
    this.drawer.setAttribute("aria-hidden", "false");
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.drawer.classList.remove("active");
    this.overlay.classList.remove("active");
    this.hamburgerBtn.classList.remove("active");
    this.body.classList.remove("drawer-open");

    // Close all submenus when closing drawer
    this.closeAllSubmenus();

    // Return focus to hamburger button
    this.hamburgerBtn.focus();

    // Remove focus trap
    this.removeFocusTrap();

    // Set ARIA attributes
    this.hamburgerBtn.setAttribute("aria-expanded", "false");
    this.drawer.setAttribute("aria-hidden", "true");
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  trapFocus() {
    const focusableElements = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    this.focusTrapHandler = (e) => {
      if (e.key === "Tab") {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", this.focusTrapHandler);
  }

  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener("keydown", this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}

// Initialize the slide drawer when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  new SlideDrawer();
});

// Export for potential use in other scripts
window.SlideDrawer = SlideDrawer;
